import { Button } from '../../../shared/ui/button';
import { Card } from '../../../shared/ui/layout';
import { DestinationsIcon, FlowsIcon, SourcesIcon } from '../../../shared/ui/icons';
import { DestinationCreate } from '../../../features/destination-create';
import { SourceCreate } from '../../../features/source-create';
import { FlowCreate } from '../../../widgets/flow-create';
import type { TFlow } from '../../../entities/flow';

interface IOrgEmptyStateProps {
  sourceCount: number;
  destinationCount: number;
  onCreated: (flow: TFlow) => void;
}

const noun = (count: number, word: string) => `${count} ${word}${count === 1 ? '' : 's'}`;

const INTRO =
  'A flow is what happens to a request after it arrives: where it goes, what gets checked or changed on the way, what happens if it fails.';

// The first card is whatever is missing: a source, then a destination, then the flow joining
// them. "Send a source to a destination" with nothing to pick would open onto a dead Create.
const nextStep = (sourceCount: number, destinationCount: number): string => {
  if (sourceCount === 0) {
    return 'Everything starts at a source, the address a sender posts to. Add one first, then say where its requests go.';
  }
  if (destinationCount === 0) {
    return `You have ${noun(sourceCount, 'source')}. Add a destination next: where its requests should go.`;
  }
  return `You have ${noun(sourceCount, 'source')} and ${noun(destinationCount, 'destination')} already.`;
};

export const OrgEmptyState = ({
  sourceCount,
  destinationCount,
  onCreated,
}: IOrgEmptyStateProps) => {
  const ready = sourceCount > 0 && destinationCount > 0;
  // Staying on Flows after either create: the counts refetch and the next card takes over.
  const noop = () => undefined;

  return (
    <div className="flex flex-1 items-center justify-center">
      <div className="max-w-form text-center">
        <p className="text-section font-semibold text-primary">No flows yet</p>
        <p className="mt-2 text-data text-muted">
          {INTRO} {nextStep(sourceCount, destinationCount)}
        </p>
        <FlowCreate
          onCreated={onCreated}
          renderTrigger={(openAt) => (
            <div className="mt-6 flex gap-3 text-left">
              {sourceCount === 0 ? (
                <Card className="flex-1 p-4">
                  <SourcesIcon size={18} />
                  <p className="mt-2 text-data font-medium text-primary">Add a source</p>
                  <p className="mt-1 text-meta text-muted">
                    Name it after the sender; its address comes back with it.
                  </p>
                  <div className="mt-3">
                    <SourceCreate onCreated={noop} label="Add a source" />
                  </div>
                </Card>
              ) : destinationCount === 0 ? (
                <Card className="flex-1 p-4">
                  <DestinationsIcon size={18} />
                  <p className="mt-2 text-data font-medium text-primary">Add a destination</p>
                  <p className="mt-1 text-meta text-muted">
                    An HTTP endpoint, or your own machine through the CLI.
                  </p>
                  <div className="mt-3">
                    <DestinationCreate onCreated={noop} label="Add a destination" />
                  </div>
                </Card>
              ) : (
                <Card className="flex-1 p-4">
                  <DestinationsIcon size={18} />
                  <p className="mt-2 text-data font-medium text-primary">
                    Send a source to a destination
                  </p>
                  <p className="mt-1 text-meta text-muted">Two picks, live as v1 straight away.</p>
                  <Button size="sm" className="mt-3" onClick={() => openAt('one-step')}>
                    Start here
                  </Button>
                </Card>
              )}
              <Card className="flex-1 p-4">
                <FlowsIcon size={18} />
                <p className="mt-2 text-data font-medium text-primary">Start from scratch</p>
                <p className="mt-1 text-meta text-muted">
                  Filters, branches, AI steps, a schedule.
                </p>
                <Button
                  size="sm"
                  variant={ready ? 'secondary' : 'ghost'}
                  className="mt-3"
                  onClick={() => openAt('scratch')}
                >
                  Open a canvas
                </Button>
              </Card>
            </div>
          )}
        />
      </div>
    </div>
  );
};
